/**
 * Tipi trasversali dell'API di sso-bck.
 *
 * Riferimenti: framework/Page.kt, framework/Methods.kt, errors/AppError.kt.
 */

/** Risposta paginata di sso-bck (framework/Page.kt). */
export interface Page<T> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
}

/** Paginazione richiesta. `pageNumber` è 1-based lato backend. */
export interface PageRequest {
  pageNumber: number;
  pageSize: number;
}

export const DEFAULT_PAGE_SIZE = 20;

/** Limite imposto da sso-bck: page-size > 100 è un errore 400. */
export const MAX_PAGE_SIZE = 100;

export const FIRST_PAGE: PageRequest = { pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE };

export function emptyPage<T>(request: PageRequest = FIRST_PAGE): Page<T> {
  return { items: [], pageNumber: request.pageNumber, pageSize: request.pageSize, totalCount: 0 };
}

export function totalPages(page: Page<unknown>): number {
  return page.pageSize > 0 ? Math.max(1, Math.ceil(page.totalCount / page.pageSize)) : 1;
}

/** Corpo d'errore di sso-bck (errors/AppError.kt), serializzato per ogni fallimento. */
export interface ApiError {
  message: string;
  errorType: string;
  data?: unknown;
  cause?: ApiError | null;
}

/**
 * `errorType` è il qualified name della classe d'errore Kotlin.
 *
 * Attenzione: sso-bck mappa su uno status HTTP solo alcuni errori (NotFound → 404,
 * InvalidCredentials → 401, UnsupportedUpdate e i parse error → 400); tutto il resto
 * esce con 500 pur essendo un errore di dominio perfettamente prevedibile
 * (UsernameAlreadyTaken, DisplayNameAlreadyUsed, SSONotAlterable...). Per questo
 * l'interpretazione degli errori si basa su `errorType` e non sullo status.
 */
export const ERROR_TYPE = {
  accountNotFound: 'dev.atlantis.sso.errors.AccountError.NotFound',
  invalidCredentials: 'dev.atlantis.sso.errors.AccountError.InvalidCredentials',
  totpNotSupported: 'dev.atlantis.sso.errors.TotpError.NotSupported',
  totpInvalidCode: 'dev.atlantis.sso.errors.TotpError.InvalidCode',
  totpInvalidChallenge: 'dev.atlantis.sso.errors.TotpError.InvalidChallenge',
  tooManyRequests: 'dev.atlantis.sso.errors.SecurityError.TooManyRequests',
  unsupportedAccountType: 'dev.atlantis.sso.errors.AccountError.UnsupportedAccountType',
  adminNotSupported: 'dev.atlantis.sso.errors.AccountError.AdminNotSupported',
  usernameAlreadyTaken: 'dev.atlantis.sso.errors.AccountError.UsernameAlreadyTaken',
  notActive: 'dev.atlantis.sso.errors.AccountError.NotActive',
  missingMetadata: 'dev.atlantis.sso.errors.AccountError.MissingMetadata',
  invalidMetadata: 'dev.atlantis.sso.errors.AccountError.InvalidMetadata',
  unsupportedUpdate: 'dev.atlantis.sso.errors.AccountError.UnsupportedUpdate',
  accountGroupNotFound: 'dev.atlantis.sso.errors.AccountGroupError.NotFound',
  accountGroupAlreadyMember: 'dev.atlantis.sso.errors.AccountGroupError.AlreadyMember',
  groupNotFound: 'dev.atlantis.sso.errors.GroupError.NotFound',
  groupDisplayNameAlreadyUsed: 'dev.atlantis.sso.errors.GroupError.DisplayNameAlreadyUsed',
  groupIsClientDefault: 'dev.atlantis.sso.errors.GroupError.IsClientDefault',
  clientNotFound: 'dev.atlantis.sso.errors.ClientError.NotFound',
  clientDisplayNameAlreadyUsed: 'dev.atlantis.sso.errors.ClientError.DisplayNameAlreadyUsed',
  ssoNotAlterable: 'dev.atlantis.sso.errors.ClientError.SSONotAlterable',
  invalidPublicKey: 'dev.atlantis.sso.errors.ClientError.InvalidPublicKey',
  invalidAutoProvisionAccountType:
    'dev.atlantis.sso.errors.ClientError.InvalidAutoProvisionAccountType',
  accessRequestNotFound: 'dev.atlantis.sso.errors.ClientAccessRequestError.NotFound',
  accessRequestInvalidGroup: 'dev.atlantis.sso.errors.ClientAccessRequestError.InvalidGroup',
  invalidDefaultGroup: 'dev.atlantis.sso.errors.ClientError.InvalidDefaultGroup',
  missingDefaultGroup: 'dev.atlantis.sso.errors.ClientError.MissingDefaultGroup',
  unexpectedDefaultGroup: 'dev.atlantis.sso.errors.ClientError.UnexpectedDefaultGroup',
  clientRedirectNotFound: 'dev.atlantis.sso.errors.ClientRedirectError.NotFound',
  redirectAlreadyRegistered:
    'dev.atlantis.sso.errors.ClientRedirectError.RedirectAlreadyRegistered',
  invalidUrl: 'dev.atlantis.sso.errors.UrlError.Invalid',
} as const;
